import { createSlice, createAction, Slice } from "@reduxjs/toolkit";
import { Banner, BannerDirectionQuery, BannerQueryParams } from "../../types";
import { RootState } from "store/store";
import { BANNER_POSITION, BANNER_TYPE } from "constants/banner";

export interface GetListBannerPayload {
	query: BannerQueryParams;
	callback?: () => void;
}
type webappReducerName = "webappBannerHorizontal" | "webappBannerVertical";

interface BannerState {
	bannerList: Banner[];
	bannerTotal: number;
	bannerDetail: Banner[];
	bannerAdList: Banner[];
	bannerAdDetail: Banner[];
	bannerAdTotal: number;
	webappHorizontalBanners: {
		[key: string]: Banner[];
	};
	webappBannerDetail?: Banner[];
}
const initialState: BannerState = {
	bannerList: [],
	bannerTotal: 0,
	bannerDetail: [],
	bannerAdList: [],
	bannerAdDetail: [],
	bannerAdTotal: 0,
	webappHorizontalBanners: {
		[BANNER_POSITION.HOMEPAGE_TOP.key]: [],
		[BANNER_POSITION.HOMEPAGE_CENTER.key]: [],
		[BANNER_POSITION.LIST_CAR_FOR_SALE.key]: [],
		[BANNER_POSITION.LIST_CAR_FOR_RENT.key]: [],
		[BANNER_POSITION.LIST_CAR_PART.key]: [],
		[BANNER_POSITION.CAR_FOR_RENT_DETAIL.key]: [],
		[BANNER_POSITION.CAR_FOR_SALE_DETAIL.key]: [],
		[BANNER_POSITION.CAR_PART_DETAIL.key]: [],
		[BANNER_POSITION.LIST_NEW_EVENT.key]: [],
		[BANNER_POSITION.LIST_SERVICE.key]: [],
		[BANNER_POSITION.CAR_STORE_DETAIL.key]: [],
		[BANNER_POSITION.LIST_COMMUNITY.key]: [],
		[BANNER_POSITION.COMMUNITY_DETAIL.key]: [],
	},
};

function createBannerSlice(
	name: webappReducerName,
): Slice<BannerState> & { selectors: any } {
	const slice = createSlice({
		name,
		initialState,
		reducers: {
			setListBanner: (state, action) => {
				state.bannerList = action.payload.data;
				state.bannerTotal = action.payload.total;
			},
			setListBannerAd: (state, action) => {
				state.bannerAdList = action.payload.data;
				state.bannerAdTotal = action.payload.total;
			},
			setDetailBannerAd: (state, action) => {
				state.bannerAdDetail = action.payload.data;
			},
			setDetailBanner: (state, action) => {
				state.bannerDetail = action.payload.data;
			},
			deleteBanner: (state, action) => {
				const { id } = action.payload;
				state.bannerList = state.bannerList.filter(el => el.id !== id);
				state.bannerTotal = state.bannerTotal - 1;
			},
			changeStatusBanner: (state, action) => {
				const { id, status } = action.payload;
				const index = state.bannerList.findIndex(el => el.id === id);
				state.bannerList[index].status = status;
			},
			changeStatusBannerAds: (state, action) => {
				const { id, status } = action.payload;
				const index = state.bannerAdList.findIndex(el => el.id === id);
				state.bannerAdList[index].status = status;
			},
			setWebappBanners: (state, action) => {
				const banners = action.payload.data;
				banners.sort((a: Banner, b: Banner) => a.order - b.order);
				const newWebappBanners: {
					[key: string]: Banner[];
				} = {
					[BANNER_POSITION.HOMEPAGE_TOP.key]: [],
				};
				banners.forEach((banner: Banner) => {
					if (banner.position) {
						if (newWebappBanners[banner.position]) {
							newWebappBanners[banner.position].push(banner);
						} else {
							newWebappBanners[banner.position] = [banner];
						}
					} else {
						newWebappBanners[BANNER_POSITION.HOMEPAGE_TOP.key].push(banner);
					}
				});
				state.webappHorizontalBanners = newWebappBanners;
			},
			setWebappBannerDetail: (state, action) => {
				state.webappBannerDetail = action.payload.data;
			},
			setWebappPositionBanners: (state, action) => {
				const { key, banners } = action.payload;
				state.webappHorizontalBanners[key] = banners;
			},
			removeBannersByGroupCode: (state, action) => {
				const updatedBanners = action.payload.data;
				const position =
					updatedBanners[0]?.position || BANNER_POSITION.HOMEPAGE_TOP.key;
				const groupCode = updatedBanners[0]?.groupCode;
				const type = updatedBanners[0]?.type;
				const newBanners = state.webappHorizontalBanners[position].filter(
					(banner: Banner) => banner.groupCode !== groupCode,
				);
				state.webappHorizontalBanners[position] = newBanners;
			},
		},
	});

	const selectBannerList = (state: RootState) => state[name].bannerList;
	const selectBannerTotal = (state: RootState) => state[name].bannerTotal;
	const selectBannerDetail = (state: RootState) => state[name].bannerDetail;
	const selectBannerAdList = (state: RootState) => state[name].bannerAdList;
	const selectBannerAdTotal = (state: RootState) => state[name].bannerAdTotal;
	const selectBannerAdDetail = (state: RootState) => state[name].bannerAdDetail;
	const selectWebappBanners = (state: RootState) =>
		state[name].webappHorizontalBanners;
	const selectWebappBannerDetail = (state: RootState) =>
		state[name].webappBannerDetail;

	const getListBannerAsync = createAction(
		`${name}/getListBannerAsync`,
		(payload: GetListBannerPayload) => ({
			payload: payload || {},
		}),
	);

	interface GetDirectionBannersPayload {
		query: BannerDirectionQuery;
		type?: string;
		callback?: () => void;
	}
	const getDirectionBannersAsync = createAction(
		`${name}/getDirectionBannersAsync`,
		(payload: GetDirectionBannersPayload) => ({
			payload: payload || {},
		}),
	);

	interface GetDetailBannerPayload {
		id: string;
		type?: string;
	}

	const getDetailBannerAsync = createAction(
		`${name}/getDetailBannerAsync`,
		(payload: GetDetailBannerPayload) => ({
			payload,
		}),
	);

	interface CreateMultipleBannerPayload {
		data: Banner[];
		callback?: () => void;
		failureCallback?: () => void;
	}
	const createMultipleBannerAsync = createAction(
		`${name}/createMultipleBannerAsync`,
		(payload: CreateMultipleBannerPayload) => ({
			payload,
		}),
	);

	interface UpdateMutipleBannerPayload {
		data: Banner[];
		callback?: () => void;
	}
	const updateMultipleBannerAsync = createAction(
		`${name}/updateMultipleBannerAsync`,
		(payload: UpdateMutipleBannerPayload) => ({
			payload,
		}),
	);

	interface UpdateStatusBannerByGroupCodePayload {
		data: {
			groupCode: string;
			status: number;
		};
		callback?: () => void;
	}
	const updateStatusBannerByGroupCodeAsync = createAction(
		`${name}/updateStatusBannerByGroupCodeAsync`,
		(payload: UpdateStatusBannerByGroupCodePayload) => ({
			payload,
		}),
	);

	interface RemoveBannersByGroupCodePayload {
		groupCode: string;
		callback?: () => void;
	}

	const removeBannersByGroupCodeAsync = createAction(
		`${name}/removeBannersByGroupCodeAsync`,
		(payload: RemoveBannersByGroupCodePayload) => ({
			payload,
		}),
	);

	interface UpdateBannersOrderPayload {
		data: {
			groupCode: string;
			order: number;
		}[];
		callback?: () => void;
		isShowMessage?: boolean;
	}

	const updateBannersOrderAsync = createAction(
		`${name}/updateBannersOrderAsync`,
		(payload: UpdateBannersOrderPayload) => ({
			payload,
		}),
	);

	return {
		...slice,
		selectors: {
			updateBannersOrderAsync,
			getListBannerAsync,
			getDirectionBannersAsync,
			getDetailBannerAsync,
			createMultipleBannerAsync,
			updateMultipleBannerAsync,
			updateStatusBannerByGroupCodeAsync,
			removeBannersByGroupCodeAsync,
			selectBannerList,
			selectBannerTotal,
			selectBannerDetail,
			selectBannerAdList,
			selectBannerAdTotal,
			selectBannerAdDetail,
			selectWebappBanners,
			selectWebappBannerDetail,
		},
	};
}
export const webappHorizontalBannerSlice = createBannerSlice(
	"webappBannerHorizontal",
);
export const webappVerticalBannerSlice = createBannerSlice(
	"webappBannerVertical",
);
export const webappHorizontalBannerReducer =
	webappHorizontalBannerSlice.reducer;
export const webappVerticalBannerReducer = webappVerticalBannerSlice.reducer;
